import React, { forwardRef, RefObject } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import { resetFilters } from "../../productSlice";
import { selectAreFiltersActive } from "../../productSelectors";
import Icon from "../../../../../common/components/Icon/Icon";

// --- Internal Sub-component: ClearFiltersButton ---
interface ClearFiltersButtonProps {
  className?: string;
}
const ClearFiltersButton: React.FC<ClearFiltersButtonProps> = ({
  className,
}) => {
  const dispatch = useAppDispatch();
  const { t, ready: areTranslationsReady } = useTranslation();
  const areFiltersActive = useAppSelector(selectAreFiltersActive);

  if (!areFiltersActive) {
    return null; // Don't render if no filters are active
  }

  const handleClear = () => {
    dispatch(resetFilters());
  };

  return (
    <button className={className} onClick={handleClear}>
      <Icon iconName="x-mark" className="icon--stroke-thickest" />
      {areTranslationsReady ? (
        <span>{t("productsArchive.sidebar.clearAll")}</span>
      ) : (
        <Skeleton width={60} height={18} inline />
      )}
    </button>
  );
};

// --- Internal Sub-component: FilterToggleButton ---
interface FilterToggleButtonProps {
  onOpen: () => void;
}
const FilterToggleButton = forwardRef<
  HTMLButtonElement,
  FilterToggleButtonProps
>(({ onOpen }, ref) => {
  const { t, ready: areTranslationsReady } = useTranslation();

  return (
    <button
      ref={ref}
      className="products-archive__filter-toggle"
      onClick={onOpen}
    >
      <Icon iconName="funnel-filled" className="icon--filled" />
      {areTranslationsReady ? (
        <span>{t("productsArchive.sidebar.filtersTitle")}</span>
      ) : (
        <Skeleton width={60} height={18} inline />
      )}
    </button>
  );
});

// --- Main Component: MobileToolbar ---
interface MobileToolbarProps {
  onOpenFilters: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
}

const MobileToolbar: React.FC<MobileToolbarProps> = ({
  onOpenFilters,
  triggerRef,
}) => {
  return (
    <div className="products-archive__mobile-toolbar">
      <FilterToggleButton ref={triggerRef} onOpen={onOpenFilters} />
      <ClearFiltersButton className="products-archive__clear-filters-btn" />
    </div>
  );
};

export default MobileToolbar;
